import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
	Drawer, DrawerContent, DrawerActions,
	Loader, ToastService,
	Modal, ModalContent
} from "@launchmetrics/lm-ui";
import K2Api from "lib/ajax/K2Api";
import DetailsForm from "components/LM/Voice/DetailsForm";
import ValidationSources from "components/LM/Voice/ValidationSearch/ValidationSources";
import ValidationAuditLogs from "components/LM/Voice/ValidationSearch/ValidationAuditLogs";
import DateFormat from "components/LM/common/DateFormat";
import TooltipWrapper from "components/LM/common/TooltipWrapper";
import './validationQueueDrawer.scss';

const k2Api = new K2Api();

function ValidationQueueDrawer({ open, searchParams, onClose }) {
	const [currentPage, setCurrentPage] = useState(1);
	const [total, setTotal] = useState(0);
	const [validated, setValidated] = useState(0);
	const [skipped, setSkipped] = useState(0);
	const [currentVoice, setCurrentVoice] = useState(null);
	const [voiceDetail, setVoiceDetail] = useState(null);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [logModalOpen, setLogModalOpen] = useState(false);
	const [errorMessage, setErrorMessage] = useState(null);

	const detailFormRef = useRef();
	const cachedVoices = useRef([]);
	const prevOpenRef = useRef(false);

	const doSearch = useCallback(async (page) => {
		const from = page - 1;
		const body = {
			from,
			size: 1,
			...(searchParams.term && { term: searchParams.term }),
			...(searchParams.type && { type: searchParams.type.split(',') }),
			...(searchParams.channel && { channel: searchParams.channel.split(',') }),
			...(searchParams.country && { country: searchParams.country.split(',') }),
			...(searchParams.labels && { labels: searchParams.labels.split(',') }),
			...(searchParams.status && { status: searchParams.status.split(',') }),
			...(searchParams.validated && { validated: searchParams.validated === 'true' }),
			...(searchParams.upload_csv_file_id && { upload_csv_file_id: searchParams.upload_csv_file_id.split(',').map(Number) }),
			...(searchParams.last_edited_by && { last_edited_by: searchParams.last_edited_by.split(',') }),
			...(searchParams.country_confidence_min && { country_confidence_min: Number(searchParams.country_confidence_min) }),
			...(searchParams.country_confidence_max && { country_confidence_max: Number(searchParams.country_confidence_max) }),
			...(searchParams.type_confidence_min && { type_confidence_min: Number(searchParams.type_confidence_min) }),
			...(searchParams.type_confidence_max && { type_confidence_max: Number(searchParams.type_confidence_max) }),
			...(searchParams.missing_mandatory_fields === 'true' && { missing_mandatory_fields: true }),
			...(searchParams.to_merge_only === 'true' && { to_merge_only: true }),
			...(searchParams.has_recent_publications === 'false' && { has_recent_publications: false }),
			...(searchParams.has_recent_brand_mentions === 'false' && { has_recent_brand_mentions: false }),
			...(searchParams.voice_name_duplicates === 'true' && { voice_name_duplicates: true }),
			...(searchParams.follower_channels && { follower_channels: searchParams.follower_channels.split(',') }),
			...(searchParams.followers_min && { followers_min: Number(searchParams.followers_min) }),
			...(searchParams.followers_max && { followers_max: Number(searchParams.followers_max) }),
			...(searchParams.last_edited_date_start && { last_edited_date_start: searchParams.last_edited_date_start }),
			...(searchParams.last_edited_date_end && { last_edited_date_end: searchParams.last_edited_date_end }),
			...(searchParams.voice_ids && { voice_ids: searchParams.voice_ids.split(',') }),
		};
		const res = await k2Api.post('/voices/search', body);
		return res.data;
	}, [searchParams]);

	const fetchVoiceDetail = useCallback(async (voice) => {
		setCurrentVoice(voice);
		if (voice) {
			const detailRes = await k2Api.get(`/voices/${voice.id}`);
			setVoiceDetail(detailRes.data);
		}
	}, []);

	const loadVoiceAtPage = useCallback(async (page) => {
		setLoading(true);
		setVoiceDetail(null);
		setErrorMessage(null);
		try {
			const index = page - 1;
			if (cachedVoices.current[index]) {
				await fetchVoiceDetail(cachedVoices.current[index]);
			} else {
				const response = await doSearch(page);
				const newVoices = response?.voices || [];
				if (response?.total) setTotal(response.total);
				newVoices.forEach((v, i) => {
					cachedVoices.current[index + i] = v;
				});
				await fetchVoiceDetail(cachedVoices.current[index]);
			}
		} catch (e) {
			const msg = e?.response?.data?.message || e?.message;
			setErrorMessage(msg || "Failed to fetch voice");
		} finally {
			setLoading(false);
		}
	}, [fetchVoiceDetail, doSearch]);

	// Initial search when drawer transitions from closed to open
	useEffect(() => {
		if (open && !prevOpenRef.current) {
			cachedVoices.current = [];
			setCurrentPage(1);
			setValidated(0);
			setSkipped(0);
			setLoading(true);
			setVoiceDetail(null);
			setErrorMessage(null);

			(async () => {
				try {
					const response = await doSearch(1);
					const voices = response?.voices || [];
					setTotal(response?.total || 0);
					cachedVoices.current = voices;
					if (voices.length > 0) {
						await fetchVoiceDetail(voices[0]);
					}
				} catch (e) {
					const msg = e?.response?.data?.message || e?.message;
					setErrorMessage(msg || "Failed to fetch voices");
				} finally {
					setLoading(false);
				}
			})();
		}
		prevOpenRef.current = open;
	}, [open, doSearch, fetchVoiceDetail]);

	const goToPage = (page) => {
		setCurrentPage(page);
		loadVoiceAtPage(page);
	};

	const goNext = () => {
		if (currentPage < total) {
			goToPage(currentPage + 1);
		} else {
			onClose();
			ToastService.show({
				id: "validation-complete",
				title: "All voices in this batch have been processed",
				type: "success",
				duration: 3000,
			});
		}
	};

	const handleSkip = () => {
		setSkipped(s => s + 1);
		goNext();
	};

	const handleSave = async () => {
		if (!detailFormRef.current) return;
		setSaving(true);
		try {
			let success = false;
			await detailFormRef.current.onSubmit((updatedVoice) => {
				setVoiceDetail(prev => ({ ...prev, ...updatedVoice }));
				success = true;
			});
			if (success) {
				ToastService.show({
					id: "voice-saved",
					title: "Voice saved successfully",
					type: "success",
					duration: 3000,
				});
			}
		} catch (e) {
			// errors handled inside DetailsForm
		} finally {
			setSaving(false);
		}
	};

	const handleValidate = async () => {
		if (!detailFormRef.current) return;
		setSaving(true);
		try {
			let success = false;
			await detailFormRef.current.onSubmit((updatedVoice) => {
				setVoiceDetail(prev => ({ ...prev, ...updatedVoice }));
				success = true;
			});
			if (success) {
				setValidated(v => v + 1);
				ToastService.show({
					id: "voice-validated",
					title: "Voice validated successfully",
					type: "success",
					duration: 3000,
				});
				goNext();
			}
		} catch (e) {
			// errors handled inside DetailsForm
		} finally {
			setSaving(false);
		}
	};

	const handleRefresh = () => {
		cachedVoices.current = [];
		setCurrentPage(1);
		setValidated(0);
		setSkipped(0);
		setLoading(true);
		setVoiceDetail(null);
		setErrorMessage(null);

		(async () => {
			try {
				const response = await doSearch(1);
				const voices = response?.voices || [];
				setTotal(response?.total || 0);
				cachedVoices.current = voices;
				if (voices.length > 0) {
					await fetchVoiceDetail(voices[0]);
				}
			} catch (e) {
				const msg = e?.response?.data?.message || e?.message;
				setErrorMessage(msg || "Failed to refresh voices");
			} finally {
				setLoading(false);
			}
		})();
	};

	const enrichment = voiceDetail?.enrichment;

	return (
		<>
		<Drawer open={open} onClose={onClose} anchor="right" containerStyleProps={{ width: '560px' }}>
			<div className="vq-drawer__fixed-header">
				<div className="vq-drawer__fixed-header-left">
					<span className="vq-drawer__fixed-title">Validation Queue</span>
					<span className="vq-drawer__fixed-subtitle">
						{validated}/{total} validated · {skipped} skipped · {Math.max(0, total - validated - skipped)} more in pool
					</span>
				</div>
				<div className="vq-drawer__fixed-header-right">
					<button className="vq-drawer__header-btn vq-drawer__log-btn" onClick={() => setLogModalOpen(true)}>
						<span className="vq-drawer__log-icon">📦</span> Log
					</button>
					<button
						className="vq-drawer__header-btn vq-drawer__nav-btn"
						disabled={currentPage <= 1}
						onClick={() => goToPage(currentPage - 1)}
					>
						‹
					</button>
					<span className="vq-drawer__page-indicator">{total === 0 ? 0 : currentPage} / {total}</span>
					<button
						className="vq-drawer__header-btn vq-drawer__nav-btn"
						disabled={currentPage >= total}
						onClick={() => goToPage(currentPage + 1)}
					>
						›
					</button>
					<button className="vq-drawer__header-btn vq-drawer__close-btn" onClick={onClose}>
						✕
					</button>
				</div>
			</div>

			<DrawerContent styleProps={{ padding: 0, paddingTop: 0 }}>
				<div className="vq-drawer__info-bar">
					<div className="vq-drawer__info-bar-left">
						<span className="vq-drawer__info-icon">📦</span>
						<span className="vq-drawer__info-text">
							Validating — {total} voices assigned · {Math.max(0, total - validated - skipped)} more in pool
						</span>
					</div>
					<button className="vq-drawer__refresh-btn" onClick={handleRefresh}>
						<span className="vq-drawer__refresh-icon">↻</span> Refresh batch
					</button>
				</div>
				{loading ? (
					<div className="vq-drawer__loader">
						<Loader size={40} />
					</div>
				) : !voiceDetail ? (
					<div className={`vq-drawer__empty${errorMessage ? ' vq-drawer__empty--error' : ''}`}>
						<p>{errorMessage || 'No results found'}</p>
					</div>
				) : voiceDetail ? (
					<div className="vq-drawer__body">
						<div className="vq-drawer__voice-header">
							<div className="vq-drawer__voice-avatar">
								{voiceDetail.name?.charAt(0).toUpperCase()}
							</div>
							<div className="vq-drawer__voice-info">
								<div className="vq-drawer__voice-name-row">
									<span className="vq-drawer__voice-title">{voiceDetail.name}</span>
									<a
										href={`/voices/${voiceDetail.id}`}
										target="_blank"
										rel="noopener noreferrer"
										className="vq-drawer__open-link"
									>
										↗ Open in DeepBlue
									</a>
								</div>
								<div className="vq-drawer__badges">
									<span className={`vq-drawer__badge vq-drawer__badge--${voiceDetail.status}`}>
										{voiceDetail.status?.charAt(0).toUpperCase() + voiceDetail.status?.slice(1)}
									</span>
									{voiceDetail.type && (
										<span className="vq-drawer__badge vq-drawer__badge--type">
											{voiceDetail.type?.charAt(0).toUpperCase() + voiceDetail.type?.slice(1)}
										</span>
									)}
									{voiceDetail.labels?.find(l => l.name?.toLowerCase() === 'mega') && (
										<span className="vq-drawer__badge vq-drawer__badge--mega">Mega</span>
									)}
								</div>
							</div>
						</div>

						<section className="vq-drawer__section">
							<h4 className="vq-drawer__section-title">DETAILS</h4>
							<div className="vq-drawer__details-grid">
								<div className="vq-drawer__detail-item">
									<span className="vq-drawer__detail-label">ID</span>
									<span className="vq-drawer__detail-value">{voiceDetail.id}</span>
								</div>
								<div className="vq-drawer__detail-item">
									<span className="vq-drawer__detail-label">COUNTRY</span>
									<span className="vq-drawer__detail-value">{voiceDetail.country?.name || '—'} {voiceDetail.country?.id ? `(${voiceDetail.country.id})` : ''}</span>
								</div>
								<div className="vq-drawer__detail-item">
									<span className="vq-drawer__detail-label">INSERTED</span>
									<span className="vq-drawer__detail-value"><DateFormat>{voiceDetail.inserted_at}</DateFormat></span>
								</div>
								<div className="vq-drawer__detail-item">
									<span className="vq-drawer__detail-label">UPDATED</span>
									<span className="vq-drawer__detail-value"><DateFormat>{voiceDetail.updated_at}</DateFormat></span>
								</div>
							</div>
						</section>

						{enrichment && (
							<section className="vq-drawer__section">
								<h4 className="vq-drawer__section-title">CONFIDENCE SCORES</h4>
								<div className="vq-drawer__confidence">
									<div className="vq-drawer__confidence-row">
										<span className="vq-drawer__confidence-label">Country</span>
										<div className="vq-drawer__confidence-bar">
											<div
												className={`vq-drawer__confidence-bar-fill ${Math.round((enrichment.country_confidence || 0) * 100) < 50 ? 'vq-drawer__confidence-bar-fill--low' : ''}`}
												style={{ width: `${Math.round((enrichment.country_confidence || 0) * 100)}%` }}
											/>
										</div>
										<span className="vq-drawer__confidence-value">{Math.round((enrichment.country_confidence || 0) * 100)}%</span>
									</div>
									<div className="vq-drawer__confidence-row">
										<span className="vq-drawer__confidence-label">Type</span>
										<div className="vq-drawer__confidence-bar">
											<div
												className={`vq-drawer__confidence-bar-fill ${Math.round((enrichment.type_confidence || 0) * 100) < 50 ? 'vq-drawer__confidence-bar-fill--low' : ''}`}
												style={{ width: `${Math.round((enrichment.type_confidence || 0) * 100)}%` }}
											/>
										</div>
										<span className="vq-drawer__confidence-value">{Math.round((enrichment.type_confidence || 0) * 100)}%</span>
									</div>
								</div>
							</section>
						)}

						<section className="vq-drawer__section">
							<h4 className="vq-drawer__section-title">EDIT</h4>
							<DetailsForm
								ref={detailFormRef}
								voice={voiceDetail}
								showToast={false}
								onChange={() => {}}
								onLoading={() => {}}
							/>
						</section>

						<section className="vq-drawer__section">
							<h4 className="vq-drawer__section-title">SOURCES</h4>
							<ValidationSources
								voiceId={voiceDetail.id}
								voiceType={voiceDetail.type}
								profiles={voiceDetail.socialmedia || {}}
								medias={voiceDetail.medias || {}}
								urls={voiceDetail.urls || []}
								onSourcesChanged={() => fetchVoiceDetail(currentVoice)}
							/>
						</section>
					</div>
				) : null}
			</DrawerContent>

			<DrawerActions>
				<div className="vq-drawer__actions">
					<button className="vq-drawer__btn vq-drawer__btn--skip" onClick={handleSkip} disabled={saving}>
						↗ Skip
					</button>
					<button className="vq-drawer__btn vq-drawer__btn--save" onClick={handleSave} disabled={saving}>
						{saving ? <Loader size={16} /> : '💾'} Save Changes
					</button>
					{voiceDetail?.validated ? (
						<TooltipWrapper placement="top" tooltip="This voice has already been validated">
							<button className="vq-drawer__btn vq-drawer__btn--validated" onClick={handleValidate} disabled={saving}>
								{saving ? <Loader size={16} /> : '✓'} Validate
							</button>
						</TooltipWrapper>
					) : (
						<button className="vq-drawer__btn vq-drawer__btn--validate" onClick={handleValidate} disabled={saving}>
							{saving ? <Loader size={16} /> : '✓'} Validate
						</button>
					)}
				</div>
			</DrawerActions>
		</Drawer>

		<Modal open={logModalOpen} onOpenChange={(isOpen) => setLogModalOpen(isOpen)}>
			<ModalContent size="large" styleProps={{ padding: 0 }}>
				{logModalOpen && currentVoice?.id && (
					<ValidationAuditLogs
						voiceId={currentVoice.id}
						voiceName={voiceDetail?.name}
					/>
				)}
			</ModalContent>
		</Modal>
		</>
	);
}

ValidationQueueDrawer.propTypes = {
	open: PropTypes.bool.isRequired,
	searchParams: PropTypes.object.isRequired,
	onClose: PropTypes.func.isRequired,
};

export default ValidationQueueDrawer;
